<script lang="ts">
import { Vue, Component } from 'vue-facing-decorator';
import NavBar from "@/component/common/NavBar.vue";
import AboutContentBlock from "@/component/project/about/AboutContentBlock.vue";
import { usePostHog } from '@/composables/usePostHog';
import { useRouter } from 'vue-router';

@Component({
  components: {NavBar, AboutContentBlock}
})
export default class About extends Vue {
  private posthog = usePostHog().posthog;
  private router = useRouter();

  mounted() {
    window.scrollTo(0, 0);
  }

  public trackMiniProjectClick(project: string) {
    this.posthog.capture('about_mini_project_clicked', { project: project });
    this.router.push({ name: project });
  }
}
</script>

<template>
  <div class="about">
    <NavBar/>
    <div class="content">
      <div class="about-page">
        <!-- Étape 1 : Parcours de formation et expériences -->
        <AboutContentBlock 
          icon="mdi-account-school-outline" 
          :title="$t('about.title')" 
          variant="teal"
        >
          <div class="text-layout">
            <p v-html="$t('about.intro1')"></p>
            <p v-html="$t('about.intro2')"></p>
            <p v-html="$t('about.intro3')"></p>
          </div>
        </AboutContentBlock>

        <!-- Étape 2 : Derniers projets réalisés -->
        <AboutContentBlock 
          icon="mdi-rocket-launch-outline" 
          :title="$t('about.projectsTitle')" 
          variant="terracotta"
        >
          <div class="mini-projects-grid">
            <div @click="trackMiniProjectClick('infrastructure')" class="mini-project-card" role="button">
              <div class="mp-header">
                <span class="mdi mdi-server-network"></span>
                <strong>{{ $t('about.projects.homelab.title') }}</strong>
              </div>
              <p>{{ $t('about.projects.homelab.desc') }}</p>
              <div class="mp-tags">
                <span>K3s</span><span>ArgoCD</span><span>Proxmox</span>
              </div>
            </div>

            <div @click="trackMiniProjectClick('ndi')" class="mini-project-card" role="button">
              <div class="mp-header">
                <span class="mdi mdi-timer-outline"></span>
                <strong>{{ $t('about.projects.ndi.title') }}</strong>
              </div>
              <p>{{ $t('about.projects.ndi.desc') }}</p>
              <div class="mp-tags">
                <span>Next.js</span><span>Spring Boot</span><span>OpenAPI</span>
              </div>
            </div>

            <div @click="trackMiniProjectClick('portfolio')" class="mini-project-card" role="button">
              <div class="mp-header">
                <span class="mdi mdi-application-cog-outline"></span>
                <strong>{{ $t('about.projects.portfolio.title') }}</strong>
              </div>
              <p>{{ $t('about.projects.portfolio.desc') }}</p>
              <div class="mp-tags">
                <span>GitLab CI</span><span>ArgoCD</span><span>K8s</span>
              </div>
            </div>

            <div @click="trackMiniProjectClick('pikuTrade')" class="mini-project-card" role="button">
              <div class="mp-header">
                <span class="mdi mdi-chart-line"></span>
                <strong>{{ $t('about.projects.pikutrade.title') }}</strong>
              </div>
              <p>{{ $t('about.projects.pikutrade.desc') }}</p>
              <div class="mp-tags">
                <span>Java</span><span>WebFlux</span><span>DeepLearning</span>
              </div>
            </div>

            <div @click="trackMiniProjectClick('sproogy')" class="mini-project-card" role="button">
              <div class="mp-header">
                <span class="mdi mdi-xml"></span>
                <strong>{{ $t('about.projects.sproogy.title') }}</strong>
              </div>
              <p>{{ $t('about.projects.sproogy.desc') }}</p>
              <div class="mp-tags">
                <span>Java</span><span>Security</span><span>Reflection</span>
              </div>
            </div>

            <div @click="trackMiniProjectClick('referendum')" class="mini-project-card" role="button">
              <div class="mp-header">
                <span class="mdi mdi-vote-outline"></span>
                <strong>{{ $t('about.projects.referendum.title') }}</strong>
              </div>
              <p>{{ $t('about.projects.referendum.desc') }}</p>
              <div class="mp-tags">
                <span>ElGamal</span><span>SOLID</span><span>Java</span>
              </div>
            </div>

            <div @click="trackMiniProjectClick('cgj')" class="mini-project-card" role="button">
              <div class="mp-header">
                <span class="mdi mdi-controller-classic-outline"></span>
                <strong>{{ $t('about.projects.cgj.title') }}</strong>
              </div>
              <p>{{ $t('about.projects.cgj.desc') }}</p>
              <div class="mp-tags">
                <span>Agile</span><span>Git</span><span>GameDev</span>
              </div>
            </div>
          </div>
        </AboutContentBlock>

        <!-- Étape 3 : Compétences -->
        <AboutContentBlock 
          icon="mdi-shield-check-outline" 
          :title="$t('about.skillsTitle')" 
          variant="stone"
        >
          <div class="skills-showcase">
            <div class="skill-box">
              <div class="sb-header">
                <span class="mdi mdi-cloud-sync"></span>
                <h3>Infra & DevOps</h3>
              </div>
              <div class="sb-pills">
                <span class="pill">Kubernetes</span><span class="pill">GitOps</span>
                <span class="pill">Terraform</span><span class="pill">Proxmox</span>
              </div>
            </div>

            <div class="skill-box">
              <div class="sb-header">
                <span class="mdi mdi-code-braces"></span>
                <h3>Backend Dev</h3>
              </div>
              <div class="sb-pills">
                <span class="pill">Java 21</span><span class="pill">Spring Boot</span>
                <span class="pill">WebFlux</span><span class="pill">REST/GraphQL</span>
              </div>
            </div>

            <div class="skill-box">
              <div class="sb-header">
                <span class="mdi mdi-database-cog-outline"></span>
                <h3>Cloud Storage</h3>
              </div>
              <div class="sb-pills">
                <span class="pill">Longhorn CSI</span><span class="pill">MinIO (S3)</span>
                <span class="pill">ZFS</span><span class="pill">PostgreSQL HA</span>
              </div>
            </div>

            <div class="skill-box">
              <div class="sb-header">
                <span class="mdi mdi-security"></span>
                <h3>SecOps</h3>
              </div>
              <div class="sb-pills">
                <span class="pill">HashiCorp Vault</span><span class="pill">Zitadel SSO</span>
                <span class="pill">NetPolicies</span><span class="pill">RBAC</span>
              </div>
            </div>

            <div class="skill-box">
              <div class="sb-header">
                <span class="mdi mdi-chart-bell-curve-cumulative"></span>
                <h3>Observability</h3>
              </div>
              <div class="sb-pills">
                <span class="pill">Prometheus</span><span class="pill">Grafana</span>
                <span class="pill">Loki</span><span class="pill">Alertmanager</span>
              </div>
            </div>

            <div class="skill-box">
              <div class="sb-header">
                <span class="mdi mdi-palette-swatch-outline"></span>
                <h3>Frontend / UI</h3>
              </div>
              <div class="sb-pills">
                <span class="pill">Vue.js 3</span><span class="pill">TypeScript</span>
                <span class="pill">Vite</span><span class="pill">CSS (LN Theme)</span>
              </div>
            </div>
          </div>
        </AboutContentBlock>

        <!-- Étape 4 : Projet professionnel -->
        <AboutContentBlock 
          icon="mdi-map-marker-distance" 
          :title="$t('about.futureTitle')" 
          variant="beige"
        >
          <div class="text-layout">
            <p v-html="$t('about.future1')"></p>
            <p v-html="$t('about.future2')"></p>
            <p v-html="$t('about.future3')"></p>
          </div>
        </AboutContentBlock>
      </div>
    </div>
  </div>
</template>

<style scoped>
.about {
  min-height: calc(100vh - var(--ln-nav-offset));
  background: transparent;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: var(--ln-content-max-width);
  margin: 0 auto;
  padding: 4rem 2rem;
  width: 100%;
}

.about-page {
  width: 100%;
  max-width: 1000px;
}

/* === MINI PROJECTS GRID === */
.mini-projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  justify-content: center;
}

.mini-project-card {
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: var(--ln-glass-surface);
  border: 1px solid var(--ln-glass-border);
  border-radius: var(--ln-radius-md);
  text-decoration: none;
  color: inherit;
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

.mini-project-card:hover {
  transform: translateY(-5px);
  background: var(--ln-glass-elevated);
  border-color: var(--ln-terracotta-subtle);
  box-shadow: 0 10px 30px var(--ln-glass-shadow);
}

.mp-header {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1rem;
}

.mp-header span.mdi {
  font-size: 1.5rem;
  color: var(--ln-terracotta-subtle);
}

.mp-header strong {
  font-size: 1.1rem;
  color: var(--ln-text-primary);
}

.mini-project-card p {
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
  text-align: left;
  flex-grow: 1;
}

.mp-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.mp-tags span {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.6rem;
  background: var(--ln-glass-base);
  border: 1px solid var(--ln-glass-border-strong);
  border-radius: 4px;
  color: var(--ln-stone-grey);
}

/* === SKILLS SHOWCASE === */
.skills-showcase {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2.5rem;
  justify-content: center;
}

.skill-box {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.sb-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid var(--ln-glass-border-strong);
}

.sb-header span.mdi {
  font-size: 1.8rem;
  color: var(--ln-stone-grey);
}

.sb-header h3 {
  font-size: 1.1rem;
  margin: 0;
  color: var(--ln-text-primary);
  text-transform: none;
  letter-spacing: 0;
}

.sb-pills {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.pill {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.4rem 0.8rem;
  background: var(--ln-glass-surface);
  border: 1px solid var(--ln-glass-border);
  border-radius: 2rem;
  color: var(--ln-text-secondary);
  transition: all var(--ln-transition-fast) ease;
}

.pill:hover {
  background: var(--ln-stone-grey);
  color: white;
  border-color: var(--ln-stone-grey);
}

.text-layout p {
  margin-bottom: 1.5rem;
  line-height: 1.8;
  text-align: justify;
}

@media (max-width: 768px) {
  .content {
    padding: 2rem 1rem;
  }
  
  .skills-showcase, .mini-projects-grid {
    grid-template-columns: 1fr;
  }
}
</style>
